import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Typography, Box, LinearProgress } from '@mui/material';

import QuestionPreview from './QuestionPreview';
import FullQuestion from './FullQuestion';
import { Uploader } from '../../utils/upload';
import {
  useCreateMediaSubmission,
  usePatchMediaSubmission,
} from '../../hooks/react-query/useMediaSubmissionData';
import config from '../../config';
import mediaSubmissionStatusOptions from '../../constants/media-submissions-status';
import {
  setQuestionVideoUploadSuccess,
  setQuestionVideoUploadError,
  setIsUploadingVideo,
  getSelectedVideoFile,
  getIsUploadingVideo,
  setMediaSubmissionError,
  getIsRetryingUpload,
  getCampaignLanguage
} from '@src/redux/modules/campaignLandingPage';
import styles from './CampaignQuestionContainer.module.less';
import { getTranslation } from '@src/translations/dictionary';

const { UPLOAD_S3_START, UPLOAD_S3_SUCCESS, UPLOAD_S3_ERROR } = mediaSubmissionStatusOptions;

const CampaignQuestionContainer = ({
  qid,
  _setIsUploadingVideo,
  _setQuestionVideoUploadError,
  _setQuestionVideoUploadSuccess,
  _setMediaSubmissionError,
  file,
  isUploadingVideo,
  isRetryingUpload,
  language
}) => {
  const {
    isSuccess: createMediaSubmissionSuccess,
    error: createMediaSubmissionError,
    isError: isCreateMediaSubmissionError,
    data: createMediaSubmissionData,
    mutate: createMediaSubmissionMutation,
  } = useCreateMediaSubmission();

  const {
    mutate: patchMediaSubmissionMutation,
  } = usePatchMediaSubmission();

  const [progress, setProgress] = React.useState(0);

  const attemptMultipartVideoUpload = () => {
    _setIsUploadingVideo(qid, true);
    const { fileKey, mediaSubmissionId } = createMediaSubmissionData;
    patchMediaSubmissionMutation({
      mediaSubmissionId: mediaSubmissionId,
      payload: {
        newStatus: UPLOAD_S3_START,
      }
    });

    const uploaderOptions = {
      file,
      baseURL: config.AWS.fileUploadApiGateway,
      chunkSize: 5,
      threadsQuantity: 5, // might be better to set this higher for huge files
      useTransferAcceleration: true,
    };
    const uploader = new Uploader(uploaderOptions);
    let percentage = undefined;
    uploader
      .onProgress(({ percentage: newPercentage }) => {
        // to avoid the same percentage to be logged twice
        if (newPercentage !== percentage) {
          percentage = newPercentage
          setProgress(percentage);
        }
      })
      .onError((error) => {
        console.error(error)
        patchMediaSubmissionMutation({
          mediaSubmissionId: mediaSubmissionId,
          payload: { newStatus: UPLOAD_S3_ERROR, details: error.message },
        });
        _setQuestionVideoUploadError(qid, error);
        _setIsUploadingVideo(qid, false);
      })
      .onComplete(() => {
        patchMediaSubmissionMutation({
          mediaSubmissionId: mediaSubmissionId,
          payload: { newStatus: UPLOAD_S3_SUCCESS },
        });
        _setQuestionVideoUploadSuccess(qid, mediaSubmissionId);
        _setIsUploadingVideo(qid, false);
        setProgress(0);
      });

    uploader.start(fileKey);
  };

  useEffect(() => {
    if (createMediaSubmissionSuccess) {
      attemptMultipartVideoUpload();
    }
  }, [createMediaSubmissionSuccess]);

  useEffect(() => {
    if (isRetryingUpload) {
      attemptMultipartVideoUpload();
    }
  }, [isRetryingUpload]);

  React.useEffect(() => {
    if (isCreateMediaSubmissionError) {
      _setMediaSubmissionError(qid, createMediaSubmissionError?.message);
    }
  }, [isCreateMediaSubmissionError]);

  const progressBarLabel = getTranslation('upload_in_progess', language);

  return (
    <section className={styles.vpQuestionContainer}>
      <section className={styles.vpQuestionPanel}>
        <FullQuestion qid={qid} createMediaSubmissionFn={createMediaSubmissionMutation} />
        <QuestionPreview qid={qid} />
        {isUploadingVideo && (
          <Box className={styles.vpProgressBar} sx={{ width: '100%' }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography sx={{ fontSize: '14px', textAlign: 'center', mt: '5px' }}>
              {progressBarLabel}
            </Typography>
          </Box>
        )}
      </section>
    </section>
  );
};

CampaignQuestionContainer.propTypes = {
  qid: PropTypes.string,
  file: PropTypes.string,
  language: PropTypes.string,
  isUploadingVideo: PropTypes.bool,
  isRetryingUpload: PropTypes.bool,
  _setIsUploadingVideo: PropTypes.func,
  _setQuestionVideoUploadError: PropTypes.func,
  _setQuestionVideoUploadSuccess: PropTypes.func,
  _setMediaSubmissionError: PropTypes.func,
};

function mapStateToProps(state, ownProps) {
  return {
    file: getSelectedVideoFile(state, ownProps.qid),
    isUploadingVideo: getIsUploadingVideo(state, ownProps.qid),
    isRetryingUpload: getIsRetryingUpload(state, ownProps.qid),
    language: getCampaignLanguage(state)
  };
}

const mapDispatchToProps = {
  _setIsUploadingVideo: setIsUploadingVideo,
  _setQuestionVideoUploadError: setQuestionVideoUploadError,
  _setQuestionVideoUploadSuccess: setQuestionVideoUploadSuccess,
  _setMediaSubmissionError: setMediaSubmissionError
};

export default connect(mapStateToProps, mapDispatchToProps)(CampaignQuestionContainer);



// WEBPACK FOOTER //
// ./src/components/CampaignLanding/CampaignQuestionContainer.jsx